import type { MeterPlacement, SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import type { InventoryMeterMountType } from '@enmon/commons/lib/tools/api/inventory-meter';
import type { OutputTechnology } from '@enmon/commons/lib/tools/types';

export const METER_IDENTIFIER_KINDS = {
  apiKey: 'api_key',
  devEui: 'dev_eui',
  imsi: 'imsi',
  serial: 'serial',
  toolsId: 'tools_id',
  unknown: 'unknown',
} as const;

export type MeterIdentifierKind = (typeof METER_IDENTIFIER_KINDS)[keyof typeof METER_IDENTIFIER_KINDS];

export const CACHED_METER_STATUSES = {
  downloaded: 'downloaded',
} as const;

export const CACHED_METER_TYPE_LABELS = {
  backend: 'Backend meter',
  unknown: 'Unknown meter',
} as const;

export interface NormalizedMeterIdentifier {
  rawValue: string;
  normalizedValue: string;
  kind: MeterIdentifierKind;
}

type CachedMeterSearchAliases = {
  commodity: SerializedCombinedMeter['commodity'];
  accessibility?: SerializedCombinedMeter['accessibility'];
  detail?: Record<string, unknown>;
  id?: number;
  mountTechnology?: OutputTechnology | null;
  mountType?: InventoryMeterMountType | null;
  placement?: MeterPlacement | null;
  registers?: unknown[];
} & Partial<
  Record<
    | 'apiKey'
    | 'buildingName'
    | 'calibrated'
    | 'combinedMeterId'
    | 'devEui'
    | 'floorName'
    | 'imsi'
    | 'internalNote'
    | 'internalSupplyPointCode'
    | 'manufactured'
    | 'ownerName'
    | 'ownerType'
    | 'remoteReadingOwner'
    | 'remoteReadingSerial'
    | 'serial'
    | 'status'
    | 'submittedBy'
    | 'toolsId'
    | 'updatedAt',
    string | null
  >
> &
  Partial<Record<'_id', string>> &
  Partial<Record<'buildingId' | 'locationId' | 'manufacturerId' | 'meterModelId', number | string | null>> &
  Partial<Record<'floorplanLat' | 'floorplanLng' | 'gpsLat' | 'gpsLng' | 'lorawanRssi' | 'nbiotRssi', number | null>> &
  Partial<Record<'newMeter' | 'requiresManualAks', boolean | null>>;

export type CachedMeterSearchData = Omit<Partial<SerializedCombinedMeter>, keyof CachedMeterSearchAliases> &
  CachedMeterSearchAliases;

export interface MeterSearchResult {
  id: string;
  data: CachedMeterSearchData;
  matchedBy: MeterIdentifierKind;
}
